import { sortBy } from 'lodash';
import { QuestionType_Enum_ } from 'models';
import { AggregatedMetricsTableData } from './AggregatedMetrics';
import { TableData } from 'Molecules/NestedTable';
import { AssessableMetrics } from './Metrics';

export const orderMetrics = (metrics: AssessableMetrics) => {
  const sortedMetrics = sortBy(metrics, ['globalOrder', 'order', 'firstLevel', 'secondLevel']);

  return sortedMetrics;
};

export const filterChildrenInFirstLevelMetrics = (
  metrics: AssessableMetrics,
) => {
  return (
    metrics.filter((metric) => {
      const hasParentInFirstLevel = metric.parentMetrics.some((p) =>
        metrics.some((m) => m.reference === p.parentMetricRef)
      );

      return !hasParentInFirstLevel;
    }) ?? []
  );
};

export const filterAggregatedChildrenInFirstLevelMetrics = (
  metrics: AggregatedMetricsTableData[]
) => {
  return (
    metrics.filter((metric) => {
      const hasParentInFirstLevel = metric.metric.parentMetrics.some((p) =>
        metrics.some((m) => m.metric.reference === p.parentMetricRef)
      );

      return !hasParentInFirstLevel;
    }) ?? []
  );
};

type Metric = AssessableMetrics[number];

export const separateQualitativeMetricsFromQuantitativeParents = (
  metrics: AssessableMetrics
): AssessableMetrics => {
  const qualMetrics: AssessableMetrics = [];

  const processMetric = (metric: Metric): Metric => {
    const isQuantitative = metric.metricType === QuestionType_Enum_.Decimal_;

    if (isQuantitative && metric.childrenMetrics?.length > 0) {
      const filteredChildrenMetrics = metric.childrenMetrics.filter((child) => {
        if (child.childMetric?.metricType === QuestionType_Enum_.LongText_) {
          qualMetrics.push({
            ...child.childMetric,
            childrenMetrics: child.childMetric.childrenMetrics?.map((grandChild) => ({
              ...grandChild,
              childMetric: grandChild.childMetric
                ? processMetric(grandChild.childMetric as Metric)
                : grandChild.childMetric,
            })),
          });
          return false;
        }
        return true;
      });
      return {
        ...metric,
        childrenMetrics: filteredChildrenMetrics.map((child) => ({
          ...child,
          childMetric: child.childMetric
            ? processMetric(child.childMetric as Metric)
            : child.childMetric,
        })),
      };
    }
    return metric;
  };

  const filteredMetrics = metrics.map(processMetric);

  return [...filteredMetrics, ...qualMetrics];
};

const getUrlParameter = (row: TableData<AggregatedMetricsTableData>) => {
  if (row?.tagType && row?.tagName) {
    return `?tagGroup=${row.tagName}`;
  } else if (!row?.tags && row?.tagName) {
    return `?tagChild=${row.tagName}`;
  } else if (row?.tags) {
    return `?tag=${row.tagName}`;
  }
  return '';
};

export const getMetricChartUrl = (data: {
  row: AggregatedMetricsTableData;
  metricRef: string;
  standardId: string;
  companyId: string | null;
  esrsAssessmentId: string;
  standardRef: string;
  disclosureRequirementRef: string;
  framework: string;
}) => {
  const {
    row,
    metricRef,
    standardId,
    companyId,
    esrsAssessmentId,
    standardRef,
    disclosureRequirementRef,
    framework,
  } = data;

  const baseMetricChartUrl = `/${companyId}/sustainability-reporting/${framework}/${esrsAssessmentId}/standard/${standardRef}/disclosure-requirement/metric/${disclosureRequirementRef}/chart/${metricRef}/${standardId}`;

  const urlParameter = getUrlParameter(row);

  return `${baseMetricChartUrl}${urlParameter}`;
};

export const flattenNarrativeMetricRows = (
  narrativeMetricsRowData: AggregatedMetricsTableData[]
): AssessableMetrics => {
  const flattenedMetrics: AssessableMetrics = [];

  const flattenRow = (row: AggregatedMetricsTableData) => {
    // Exclude irrelevant rows: additional info, additional info button, and locked rows, return  only metrics
    if (row.isAdditionalInfo || row.isAdditionalInfoButton || row.locked) {
      return;
    }
    if (!row.subRows || row.subRows?.length === 0) {
      flattenedMetrics.push(row.metric);
    } else {
      if (row.metric.metricType === QuestionType_Enum_.YesNo_) {
        flattenedMetrics.push(row.metric);
      }
      row.subRows?.forEach((subRow) => {
        flattenRow(subRow);
      });
    }
  };
  narrativeMetricsRowData.forEach((row) => {
    flattenRow(row);
  });

  return flattenedMetrics;
};
